import { useState, useEffect, useMemo } from 'react';
import { useMobile } from '../hooks/useMobile';
import ImapSettings from '../components/orders/ImapSettings';

// ─── Shared bits ─────────────────────────────────────────────────────────────

const STATUS_STYLE = {
  delivered: { bg: 'rgba(52,211,153,0.15)',  color: '#34d399' },
  shipped:   { bg: 'rgba(99,102,241,0.15)',  color: '#a5b4fc' },
  ordered:   { bg: 'rgba(251,191,36,0.15)',  color: '#fcd34d' },
  payment:   { bg: 'rgba(251,146,60,0.15)',  color: '#fb923c' },
  cancelled: { bg: 'rgba(248,113,113,0.15)', color: '#f87171' },
  refunded:  { bg: 'rgba(168,85,247,0.15)',  color: '#c084fc' },
  other:     { bg: 'rgba(100,116,139,0.15)', color: '#64748b' },
};

const STATUS_RANK = { cancelled: 6, refunded: 5, delivered: 4, shipped: 3, payment: 2, ordered: 1, other: 0 };

// An order is "stuck" if it was confirmed but no shipping/delivery/cancellation
// email ever followed and enough time has passed that it should have moved.
const STUCK_DAYS = 7;
function isStuck(o) {
  return o.status === 'ordered' && (Date.now() - new Date(o.date)) > STUCK_DAYS * 86400000;
}
function isCancelled(o) { return o.status === 'cancelled' || o.status === 'refunded'; }
function isKept(o)      { return o.status === 'delivered' || o.status === 'shipped'; }
// Killed without ever being confirmed — the only email that arrived was the
// cancellation. A different failure mode from being confirmed then pulled
// weeks later, and the reason no address can be recovered for these.
function neverConfirmed(o) {
  const types = (o.emails || []).map(e => e.type);
  return types.length > 0 && !types.includes('confirmation') && types.includes('cancellation');
}
// Payment authorisation failed and the order never moved — dead in practice,
// Pokémon Center just never sends a cancellation for these.
function isPaymentRisk(o) { return o.status === 'payment'; }

// ─── Physical property grouping ──────────────────────────────────────────────
// Every order deliberately varies the spelling of the same house ("137F
// Whitehalle RodVC", "137K Whitehal RoadUF", "137N Wh1tehall RoadIY"), so
// grouping on the literal string gives one group per order and no usable rate.
// Collapse to the real property: house number + street stem + postcode district,
// with digit-for-letter substitutions undone.

const NUM_WORDS = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9,
  ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16,
  seventeen: 17, eighteen: 18, nineteen: 19, twenty: 20, thirty: 30, forty: 40, fifty: 50,
  sixty: 60, seventy: 70, eighty: 80, ninety: 90,
};

// Some entries spell the house number out ("One Hundred and Thirty Seven"),
// sometimes with a junk letter appended ("TwoO", "NineE").
function wordsToNumber(text) {
  let total = 0, current = 0, seen = false;
  for (const raw of text.toLowerCase().split(/[^a-z]+/)) {
    const w = NUM_WORDS[raw] !== undefined ? raw
      : (raw.length > 3 && NUM_WORDS[raw.slice(0, -1)] !== undefined ? raw.slice(0, -1) : null);
    if (w) { current += NUM_WORDS[w]; seen = true; continue; }
    if (raw === 'hundred' || raw === 'hundredd') { current = (current || 1) * 100; seen = true; continue; }
    if (raw === 'and' || raw === '') continue;
    if (seen) break; // number words must be contiguous, at the start of the line
  }
  total += current;
  return seen && total > 0 ? String(total) : '';
}

function propertyKey(address) {
  if (!address) return null;
  const text = (address.lines || []).join(' ');
  // Postcode district (B21 9AX → b21). The obfuscation varies the unit part —
  // the same house appears as B21 9AX, B21 9AU, B21 9BB — but never the district.
  const outward = (address.postcode || '').replace(/\s+/g, '').slice(0, -3).toLowerCase()
    || (text.match(/\b([A-Z]{1,2}\d{1,2})[A-Z\d]?\s*\d[A-Z]{2}\b/i) || [])[1]?.toLowerCase()
    || '';
  // House number, with the postcode masked out first. A trailing letter is
  // noise ("137K" and "137H" are both 137). The street name is too corrupted to
  // key on — "Wh1tehall"/"Whitehalle"/"Whitehal" never normalise to one string,
  // since the digit 1 stands for i here and l elsewhere — so district+number is
  // the stable identity.
  const masked = text.replace(/\b[A-Z]{1,2}\d{1,2}[A-Z\d]?\s*\d[A-Z]{2}\b/gi, ' ');
  const num = (masked.match(/\b(\d{1,4})[a-z]?\b/i) || [])[1]
    || wordsToNumber(masked.replace(/\b(?:house|door|flat|apt|no|number)\b\.?/gi, ' '));
  return `${outward}|${num}`;
}

// ─── Address decomposition ───────────────────────────────────────────────────
// Split an entry into the parts that actually vary between orders, so two
// entries for the same house can be compared column by column instead of by
// reading two long strings.

const PREFIX_RE = /^\s*((?:house|door)\s*no\.?|flat|apt)\s*/i;

function decomposeAddress(address) {
  if (!address) return null;
  const first = (address.lines || [])[0] || '';
  const pc    = (address.postcode || '').toUpperCase();

  const prefixMatch = first.match(PREFIX_RE);
  const prefix = prefixMatch ? prefixMatch[1].replace(/\s+/g, ' ').trim() : '—';

  // Strip prefix and any postcode occurrences, then read the remaining tokens
  const rest = first
    .replace(PREFIX_RE, ' ')
    .replace(/\b[A-Z]{1,2}\d{1,2}[A-Z\d]?\s*\d[A-Z]{2}\b/gi, ' ')
    .replace(/[,]/g, ' ')
    .split(/\s+/).filter(Boolean);

  const number = rest.find(t => /^\d/.test(t)) || '—';
  const after  = rest.slice(rest.indexOf(number) + 1);

  return {
    prefix,
    number,
    street:   after[0] || '—',
    type:     after[1] || '—',
    locality: after.slice(2).join(' ') || '—',
    postcode: pc || '—',
    // A digit standing in for a letter inside a word — "Wh1tehall", "N3wcrest"
    digitSwap: after.some(t => /[a-z][0-9]|[0-9][a-z]/i.test(t)),
  };
}

function propertyLabel(address) {
  if (!address) return 'No address in email';
  const line = (address.lines || []).find(Boolean) || address.raw || '';
  const district = (address.postcode || '').replace(/\s+/g, '').slice(0, -3).toUpperCase();
  return `${line.replace(/\s+/g, ' ').trim()}${district ? ` · ${district}` : ''}`;
}

const CARD = {
  background: '#1e293b',
  borderRadius: 14,
  border: '1px solid rgba(255,255,255,0.07)',
};

function StatusBadge({ status, stuck }) {
  const s = stuck ? { bg: 'rgba(251,146,60,0.15)', color: '#fb923c' } : (STATUS_STYLE[status] || STATUS_STYLE.other);
  return (
    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color, textTransform: 'capitalize' }}>
      {stuck ? 'stuck' : status}
    </span>
  );
}

function fmtDate(iso)      { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
function fmtDateShort(iso) { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); }
function parseAmount(str)  { const m = (str || '').match(/[\d.]+/); return m ? parseFloat(m[0]) : 0; }
function toInputDate(d)    { return d.toISOString().slice(0, 10); }

// Split a date range into monthly batches to keep each API call under the timeout
function buildBatches(sinceStr, untilStr) {
  const since = new Date(sinceStr);
  const until = new Date(untilStr);
  until.setHours(23, 59, 59, 999);
  const batches = [];
  let cursor = new Date(since);
  while (cursor < until) {
    const next = new Date(cursor);
    next.setMonth(next.getMonth() + 1);
    const end = next < until ? next : until;
    batches.push({
      since: cursor.toISOString(),
      until: end.toISOString(),
      label: cursor.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
    });
    cursor = next;
  }
  return batches.length ? batches : [{ since: since.toISOString(), until: until.toISOString(), label: 'range' }];
}

// Merge a batch into the accumulated map, deduping across batches by orderId
function mergeInto(map, orders) {
  orders.forEach(o => {
    const key = o.orderId ? `order:${o.orderId}` : o.id;
    const prev = map.get(key);
    if (!prev) { map.set(key, o); return; }
    const base  = new Date(o.date) < new Date(prev.date) ? o : prev;
    const newer = base === o ? prev : o;
    map.set(key, {
      ...base,
      status:      (STATUS_RANK[newer.status] || 0) > (STATUS_RANK[base.status] || 0) ? newer.status : base.status,
      amount:      base.amount || newer.amount,
      address:     base.address || newer.address,
      addressKey:  base.addressKey || newer.addressKey,
      items:       [...new Set([...(base.items || []), ...(newer.items || [])])].slice(0, 6),
      cancelledAt: base.cancelledAt || newer.cancelledAt,
      emailCount:  (o.emailCount || 1) + (prev.emailCount || 1),
      emails:      [...(base.emails || []), ...(newer.emails || [])].sort((a, b) => new Date(a.date) - new Date(b.date)),
    });
  });
}

// ─── Address analysis ────────────────────────────────────────────────────────

function buildAddressGroups(orders) {
  const groups = new Map();
  orders.forEach(o => {
    const key = propertyKey(o.address) || 'unknown';
    if (!groups.has(key)) groups.set(key, { key, orders: [], names: new Set(), accounts: new Set(), variants: new Set(), address: null });
    const g = groups.get(key);
    g.orders.push(o);
    if (o.address) {
      g.address = g.address || o.address;
      if (o.address.name) g.names.add(o.address.name);
      g.variants.add((o.address.lines || []).join(', '));
    }
    if (o.accountEmail) g.accounts.add(o.accountEmail);
  });

  return [...groups.values()]
    .map(g => {
      const cancelled = g.orders.filter(isCancelled).length;
      const payment   = g.orders.filter(isPaymentRisk).length;
      const kept      = g.orders.filter(isKept).length;
      const stuck     = g.orders.filter(isStuck).length;
      // Success rate counts only orders that actually reached an outcome —
      // pending preorders would otherwise drag every rate down.
      const decided   = kept + cancelled + payment;
      const total     = g.orders.reduce((s, o) => s + parseAmount(o.amount), 0);
      return {
        ...g,
        names:      [...g.names],
        accounts:   [...g.accounts],
        variants:   [...g.variants],
        kept, cancelled, payment, stuck, decided,
        successRate: decided ? kept / decided : null,
        cancelRate:  decided ? cancelled / decided : 0,
        paymentRate: decided ? payment / decided : 0,
        totalValue: total,
      };
    })
    .sort((a, b) => (a.successRate ?? 2) - (b.successRate ?? 2) || b.orders.length - a.orders.length);
}

// ─── Variant breakdown within one property ───────────────────────────────────
// Each distinct entry that was typed at this address, with how it fared. Most
// entries are used only once or twice, so the counts matter more than the
// percentage — the table is for spotting which components the cancelled
// entries share.

function buildVariants(orders) {
  const map = new Map();
  orders.forEach(o => {
    const key = (o.address?.lines || []).join(', ') || '(none)';
    if (!map.has(key)) map.set(key, { key, orders: [], parts: decomposeAddress(o.address), names: new Set() });
    map.get(key).orders.push(o);
    if (o.address?.name) map.get(key).names.add(o.address.name);
  });

  return [...map.values()].map(v => {
    const kept      = v.orders.filter(isKept).length;
    const cancelled = v.orders.filter(isCancelled).length;
    const payment   = v.orders.filter(isPaymentRisk).length;
    const decided   = kept + cancelled + payment;
    return {
      ...v,
      names: [...v.names],
      kept, cancelled, payment, decided,
      pending: v.orders.length - decided,
      successRate: decided ? kept / decided : null,
    };
  }).sort((a, b) => (a.successRate ?? 2) - (b.successRate ?? 2) || b.orders.length - a.orders.length);
}

const VCELL = { padding: '7px 10px', whiteSpace: 'nowrap', verticalAlign: 'top' };

function VariantTable({ variants, isMobile }) {
  const anyBad = variants.some(v => v.cancelled + v.payment > 0);
  return (
    <div style={{ overflowX: 'auto', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <div style={{ padding: isMobile ? '10px 14px 4px' : '12px 20px 4px', fontSize: 11, color: '#64748b' }}>
        {variants.length} address entr{variants.length !== 1 ? 'ies' : 'y'} used at this property
        {anyBad && ' — compare the components of the failed rows against the successful ones'}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr style={{ background: 'rgba(0,0,0,0.2)' }}>
            {['Outcome', 'No.', 'Prefix', 'Street', 'Type', 'Locality', 'Postcode', 'Names'].map(h => (
              <th key={h} style={{ ...VCELL, textAlign: 'left', fontWeight: 500, color: '#64748b' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {variants.map(v => {
            const bad = v.cancelled + v.payment;
            const p = v.parts || {};
            return (
              <tr key={v.key} style={{
                borderTop: '1px solid rgba(255,255,255,0.04)',
                background: bad > 0 ? 'rgba(248,113,113,0.06)' : 'transparent',
              }}>
                <td style={VCELL}>
                  <span style={{ color: bad > 0 ? '#f87171' : v.kept ? '#34d399' : '#64748b', fontWeight: 700 }}>
                    {bad > 0 ? `${bad} failed` : v.kept ? `${v.kept} kept` : '—'}
                  </span>
                  {v.pending > 0 && <span style={{ color: '#64748b' }}> · {v.pending} pending</span>}
                </td>
                <td style={{ ...VCELL, color: '#e2e8f0', fontFamily: 'monospace' }}>{p.number}</td>
                <td style={{ ...VCELL, color: '#94a3b8' }}>{p.prefix}</td>
                <td style={{ ...VCELL, color: p.digitSwap ? '#fb923c' : '#e2e8f0', fontWeight: p.digitSwap ? 700 : 400 }}>
                  {p.street}{p.digitSwap && <span title="digit used in place of a letter"> ⚠</span>}
                </td>
                <td style={{ ...VCELL, color: '#94a3b8' }}>{p.type}</td>
                <td style={{ ...VCELL, color: '#64748b', maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.locality}</td>
                <td style={{ ...VCELL, color: '#94a3b8', fontFamily: 'monospace' }}>{p.postcode}</td>
                <td style={{ ...VCELL, color: '#64748b', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.names.join(', ')}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Pattern comparison across every address ─────────────────────────────────
// Which way of writing an address correlates with cancellation. Respects the
// active product filter, so a single release can be judged on its own.

function buildPatterns(orders) {
  const withAddr = orders.filter(o => o.address);
  const dims = [
    ['Digit in place of a letter (Wh1tehall)', o => decomposeAddress(o.address).digitSwap ? 'yes' : 'no'],
    ['Prefix style',                           o => decomposeAddress(o.address).prefix],
    ['Postcode used',                          o => decomposeAddress(o.address).postcode],
    ['Street spelling',                        o => decomposeAddress(o.address).street],
  ];

  return dims.map(([label, fn]) => {
    const buckets = new Map();
    withAddr.forEach(o => {
      let k; try { k = fn(o); } catch { return; }
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k).push(o);
    });
    const rows = [...buckets.entries()].map(([value, list]) => {
      const kept = list.filter(isKept).length;
      const bad  = list.filter(o => isCancelled(o) || isPaymentRisk(o)).length;
      const decided = kept + bad;
      return { value, total: list.length, kept, bad, decided, successRate: decided ? kept / decided : null };
    })
      .filter(r => r.decided > 0)
      .sort((a, b) => a.successRate - b.successRate || b.decided - a.decided);
    return { label, rows };
  }).filter(d => d.rows.length > 1);
}

function PatternsTab({ orders, isMobile }) {
  const dims = buildPatterns(orders);
  if (!dims.length) return (
    <div style={{ ...CARD, padding: 32, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
      Not enough decided orders to compare address patterns.
    </div>
  );
  return (
    <>
      <div style={{ ...CARD, padding: '10px 14px', fontSize: 12, marginBottom: 14, color: '#94a3b8', lineHeight: 1.6 }}>
        Success rate for each way of writing an address, counting only orders that reached an outcome.
        <span style={{ color: '#fcd34d' }}> Read these across properties with care:</span> a style
        used mostly at one property inherits that property's rate, so a difference here can reflect
        <em> where</em> it was used rather than the style itself. Check a single property's own table
        (Addresses → expand) before concluding, and ignore rows with few decided orders.
      </div>
      {dims.map(dim => (
        <div key={dim.label} style={{ ...CARD, marginBottom: 12, overflow: 'hidden' }}>
          <div style={{ padding: isMobile ? '12px 14px' : '14px 20px 10px', fontSize: 13, fontWeight: 600, color: '#f1f5f9' }}>
            {dim.label}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'rgba(0,0,0,0.2)' }}>
                  {['Value', 'Success', 'Kept', 'Failed', 'Decided', 'Total'].map(h => (
                    <th key={h} style={{ ...VCELL, textAlign: 'left', fontWeight: 500, color: '#64748b' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dim.rows.slice(0, 12).map(r => (
                  <tr key={r.value} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ ...VCELL, color: '#e2e8f0', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.value}</td>
                    <td style={{ ...VCELL, fontWeight: 700, color: successColor(r.successRate) }}>{Math.round(r.successRate * 100)}%</td>
                    <td style={{ ...VCELL, color: '#34d399' }}>{r.kept}</td>
                    <td style={{ ...VCELL, color: r.bad ? '#f87171' : '#64748b' }}>{r.bad}</td>
                    <td style={{ ...VCELL, color: '#64748b' }}>{r.decided}</td>
                    <td style={{ ...VCELL, color: '#64748b' }}>{r.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </>
  );
}

// ─── Product analysis ────────────────────────────────────────────────────────
// One group per distinct product name; an order with 3 items appears in 3
// groups. Grouping key is the normalised name so minor case/spacing variants
// across emails collapse together.

function productKey(name) {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function buildProductGroups(orders) {
  const groups = new Map();
  orders.forEach(o => {
    const items = (o.items && o.items.length) ? o.items : ['(product not identified)'];
    items.forEach(name => {
      const key = productKey(name) || 'unknown';
      if (!groups.has(key)) groups.set(key, { key, name, orders: [], accounts: new Set(), addresses: new Set() });
      const g = groups.get(key);
      g.orders.push(o);
      if (o.accountEmail) g.accounts.add(o.accountEmail);
      if (o.addressKey)   g.addresses.add(o.addressKey);
    });
  });

  return [...groups.values()]
    .map(g => {
      const cancelled = g.orders.filter(isCancelled).length;
      const stuck     = g.orders.filter(isStuck).length;
      return {
        ...g,
        accounts:   [...g.accounts],
        addresses:  g.addresses.size,
        cancelled,
        stuck,
        cancelRate:  g.orders.length ? cancelled / g.orders.length : 0,
        stuckRate:   g.orders.length ? stuck / g.orders.length : 0,
        problemRate: g.orders.length ? (cancelled + stuck) / g.orders.length : 0,
        totalValue: g.orders.reduce((s, o) => s + parseAmount(o.amount), 0),
      };
    })
    .sort((a, b) => b.orders.length - a.orders.length);
}

function rateColor(rate) {
  if (rate >= 0.75) return '#f87171';
  if (rate >= 0.4)  return '#fcd34d';
  return '#34d399';
}

// Success rate reads the other way round: high is good.
function successColor(rate) {
  if (rate === null)  return '#64748b';
  if (rate >= 0.9)  return '#34d399';
  if (rate >= 0.6)  return '#fcd34d';
  return '#f87171';
}

// ─── Stats ───────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{ ...CARD, flex: 1, minWidth: 120, padding: '14px 18px' }}>
      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: accent || '#f1f5f9', letterSpacing: '-0.5px' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function StatsRow({ orders, groups }) {
  const cancelled = orders.filter(isCancelled).length;
  const payment   = orders.filter(isPaymentRisk).length;
  const kept      = orders.filter(isKept).length;
  const decided   = kept + cancelled + payment;
  const success   = decided ? Math.round((kept / decided) * 100) : null;
  const addresses = groups.filter(g => g.key !== 'unknown').length;
  const accounts  = new Set(orders.map(o => o.accountEmail).filter(Boolean)).size;
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
      <StatCard label="Orders" value={orders.length} sub={`${decided} with an outcome`} accent="#818cf8" />
      <StatCard label="Success rate" value={success === null ? '—' : `${success}%`} sub={`${kept} kept of ${decided}`} accent={successColor(success === null ? null : success / 100)} />
      <StatCard label="Cancelled" value={cancelled} sub={`${orders.filter(neverConfirmed).length} never confirmed`} accent={cancelled ? '#f87171' : '#34d399'} />
      <StatCard label="Payment failed" value={payment} sub="card not authorised" accent={payment ? '#fb923c' : '#34d399'} />
      <StatCard label="Properties" value={addresses} sub="distinct ship-to" />
      <StatCard label="Accounts" value={accounts} sub="unique inbox emails" />
    </div>
  );
}

// ─── Orders table ────────────────────────────────────────────────────────────

function shortAddress(address) {
  if (!address) return '—';
  const line1 = address.lines?.[0] || '';
  return [line1, address.postcode].filter(Boolean).join(', ') || address.raw || '—';
}

function OrdersTable({ orders, isMobile }) {
  const total = orders.reduce((s, o) => s + parseAmount(o.amount), 0);
  return (
    <div style={{ ...CARD, overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: isMobile ? '14px 14px 10px' : '16px 20px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#818cf8' }}>Pokémon Center orders</div>
        <div style={{ fontSize: 12, color: '#64748b' }}>{orders.length} order{orders.length !== 1 ? 's' : ''} · £{total.toFixed(2)}</div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'rgba(0,0,0,0.2)' }}>
              {['Date', 'Order', 'Status', 'Total', 'Name', 'Address', 'Account', 'Items'].map(h => (
                <th key={h} style={{ padding: isMobile ? '8px 10px' : '9px 14px', textAlign: 'left', fontWeight: 500, color: '#64748b', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.map((o, i) => (
              <tr key={o.id} style={{ borderTop: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                <td style={{ padding: isMobile ? '8px 10px' : '9px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDateShort(o.date)}</td>
                <td style={{ padding: isMobile ? '8px 10px' : '9px 14px', color: '#475569', fontFamily: 'monospace', fontSize: 10, whiteSpace: 'nowrap' }}>{o.orderId || '—'}</td>
                <td style={{ padding: isMobile ? '8px 10px' : '9px 14px', whiteSpace: 'nowrap' }}>
                  <StatusBadge status={o.status} stuck={isStuck(o)} />
                  {neverConfirmed(o) && (
                    <span title="No confirmation email was ever received for this order" style={{ marginLeft: 5, fontSize: 10, color: '#fb923c' }}>never confirmed</span>
                  )}
                </td>
                <td style={{ padding: isMobile ? '8px 10px' : '9px 14px', color: '#f1f5f9', fontWeight: 600, whiteSpace: 'nowrap' }}>{o.amount || '—'}</td>
                <td style={{ padding: isMobile ? '8px 10px' : '9px 14px', color: '#e2e8f0', whiteSpace: 'nowrap' }}>{o.address?.name || '—'}</td>
                <td style={{ padding: isMobile ? '8px 10px' : '9px 14px', color: '#94a3b8', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shortAddress(o.address)}</td>
                <td style={{ padding: isMobile ? '8px 10px' : '9px 14px', color: '#475569', whiteSpace: 'nowrap', fontSize: 11 }}>{o.accountEmail || o.account}</td>
                <td style={{ padding: isMobile ? '8px 10px' : '9px 14px', color: '#94a3b8', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(o.items || []).join(' · ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Address analysis tab ────────────────────────────────────────────────────

function AddressCard({ group, isMobile }) {
  const [open, setOpen] = useState(false);
  const sc = successColor(group.successRate);
  const isUnknown = group.key === 'unknown';
  const pct = group.successRate === null ? '—' : `${Math.round(group.successRate * 100)}%`;

  return (
    <div style={{ ...CARD, marginBottom: 12, overflow: 'hidden', border: group.successRate !== null && group.successRate < 0.5 && group.decided > 1 ? '1px solid rgba(248,113,113,0.35)' : CARD.border }}>
      <div onClick={() => setOpen(o => !o)} style={{ padding: isMobile ? '14px' : '16px 20px', cursor: 'pointer' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', marginBottom: 3 }}>
              {isUnknown ? 'No address found in email' : propertyLabel(group.address)}
            </div>
            {!isUnknown && group.variants.length > 1 && (
              <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.5 }}>
                {group.variants.length} address spellings used at this property
              </div>
            )}
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
              {group.names.length > 0 && (
                <>Names: <span style={{ color: '#e2e8f0' }}>{group.names.slice(0, 6).join(', ')}{group.names.length > 6 ? ` +${group.names.length - 6}` : ''}</span> · </>
              )}
              <span style={{ color: '#e2e8f0' }}>{group.accounts.length}</span> account{group.accounts.length !== 1 ? 's' : ''}
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: sc, letterSpacing: '-0.5px' }}>{pct}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>success rate</div>
            <div style={{ fontSize: 12, fontWeight: 700 }}>
              <span style={{ color: '#34d399' }}>{group.kept} kept</span>
              {' · '}
              <span style={{ color: group.cancelled ? '#f87171' : '#64748b' }}>{group.cancelled} cancelled</span>
              {group.payment > 0 && <> · <span style={{ color: '#fb923c' }}>{group.payment} payment</span></>}
            </div>
            <div style={{ fontSize: 11, color: '#64748b' }}>{group.orders.length} orders · £{group.totalValue.toFixed(2)}</div>
          </div>
        </div>

        {/* Outcome bar: green = kept, red = cancelled, orange = payment failure */}
        <div style={{ height: 5, background: 'rgba(255,255,255,0.05)', borderRadius: 4, marginTop: 12, overflow: 'hidden', display: 'flex' }}>
          <div style={{ height: '100%', background: '#34d399', width: `${(group.successRate || 0) * 100}%`, transition: 'width 0.3s ease' }} />
          <div style={{ height: '100%', background: '#f87171', width: `${group.cancelRate * 100}%`, transition: 'width 0.3s ease' }} />
          <div style={{ height: '100%', background: '#fb923c', width: `${group.paymentRate * 100}%`, transition: 'width 0.3s ease' }} />
        </div>
      </div>

      {open && <VariantTable variants={buildVariants(group.orders)} isMobile={isMobile} />}

      {open && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          {group.orders.map(o => (
            <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: isMobile ? '9px 14px' : '10px 20px', borderTop: '1px solid rgba(255,255,255,0.03)', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: '#64748b', width: 60, flexShrink: 0 }}>{fmtDateShort(o.date)}</span>
              <StatusBadge status={o.status} stuck={isStuck(o)} />
              <span style={{ fontSize: 12, fontWeight: 600, color: '#f1f5f9', width: 70 }}>{o.amount || '—'}</span>
              <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#475569' }}>{o.orderId || ''}</span>
              <span style={{ fontSize: 11, color: '#64748b', flex: 1, minWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {o.address?.name ? `${o.address.name} — ` : ''}{(o.address?.lines || [])[0] || ''}
              </span>
              <span style={{ fontSize: 11, color: '#94a3b8', flex: 1, minWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {(o.items || []).join(' · ')}
              </span>
              {o.cancelledAt && <span style={{ fontSize: 10, color: '#f87171' }}>cancelled {fmtDateShort(o.cancelledAt)}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Orders whose only email was a cancellation have no address anywhere — no
// confirmation was ever sent, so the address cannot be recovered from any
// inbox. Keep them out of the property ranking (they are not a property with a
// 0% success rate) and report them separately.
function UnattributableCard({ group, isMobile }) {
  const cancelledOnly = group.orders.filter(o => (o.emails || []).every(e => e.type === 'cancellation')).length;
  return (
    <div style={{ ...CARD, marginBottom: 12, padding: isMobile ? '14px' : '16px 20px', borderStyle: 'dashed' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>
        {group.orders.length} order{group.orders.length !== 1 ? 's' : ''} with no determinable address
      </div>
      <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
        {cancelledOnly > 0 && <>{cancelledOnly} of these produced <strong style={{ color: '#94a3b8' }}>only a cancellation email</strong> — no order confirmation was ever received, and cancellation emails carry no address block. </>}
        These cannot be attributed to a property, so they are excluded from the rates above and counted only in the totals.
      </div>
      <div style={{ display: 'flex', gap: 14, marginTop: 10, fontSize: 12, flexWrap: 'wrap' }}>
        <span style={{ color: '#f87171', fontWeight: 600 }}>{group.cancelled} cancelled</span>
        {group.payment > 0 && <span style={{ color: '#fb923c', fontWeight: 600 }}>{group.payment} payment failed</span>}
        <span style={{ color: '#64748b' }}>{new Set(group.accounts).size} inboxes</span>
      </div>
    </div>
  );
}

function AddressesTab({ groups, isMobile }) {
  if (!groups.length) return null;
  const properties    = groups.filter(g => g.key !== 'unknown');
  const unattributable = groups.find(g => g.key === 'unknown');
  const flagged = properties.filter(g => g.successRate !== null && g.successRate < 0.5 && g.decided > 1);
  return (
    <>
      {flagged.length > 0 && (
        <div style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10, padding: '10px 14px', fontSize: 12, marginBottom: 14, color: '#f87171', fontWeight: 500 }}>
          ⚠ {flagged.length} propert{flagged.length !== 1 ? 'ies' : 'y'} below a 50% success rate — likely flagged by Pokémon Center.
        </div>
      )}
      {properties.map(g => <AddressCard key={g.key} group={g} isMobile={isMobile} />)}
      {unattributable && <UnattributableCard group={unattributable} isMobile={isMobile} />}
    </>
  );
}

// ─── Product analysis tab ────────────────────────────────────────────────────

function ProductCard({ group, isMobile }) {
  const [open, setOpen] = useState(false);
  const rc = rateColor(group.problemRate);

  return (
    <div style={{ ...CARD, marginBottom: 12, overflow: 'hidden', border: group.problemRate >= 0.75 && group.orders.length > 1 ? '1px solid rgba(248,113,113,0.35)' : CARD.border }}>
      <div onClick={() => setOpen(o => !o)} style={{ padding: isMobile ? '14px' : '16px 20px', cursor: 'pointer' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', marginBottom: 3 }}>{group.name}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              {group.addresses} address{group.addresses !== 1 ? 'es' : ''} · Accounts: <span style={{ color: '#e2e8f0' }}>{group.accounts.length ? group.accounts.join(', ') : '—'}</span>
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#818cf8' }}>{group.orders.length}<span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}> orders</span></div>
            <div style={{ fontSize: 12, fontWeight: 700 }}>
              <span style={{ color: group.cancelled ? '#f87171' : '#64748b' }}>{group.cancelled} cancelled</span>
              {' · '}
              <span style={{ color: group.stuck ? '#fb923c' : '#64748b' }}>{group.stuck} stuck</span>
              {' · '}
              <span style={{ color: rc }}>{Math.round(group.problemRate * 100)}%</span>
            </div>
            <div style={{ fontSize: 11, color: '#64748b' }}>£{group.totalValue.toFixed(2)}</div>
          </div>
        </div>

        {/* Problem bar: red = cancelled, orange = stuck */}
        <div style={{ height: 5, background: 'rgba(255,255,255,0.05)', borderRadius: 4, marginTop: 12, overflow: 'hidden', display: 'flex' }}>
          <div style={{ height: '100%', background: '#f87171', width: `${group.cancelRate * 100}%`, transition: 'width 0.3s ease' }} />
          <div style={{ height: '100%', background: '#fb923c', width: `${group.stuckRate * 100}%`, transition: 'width 0.3s ease' }} />
        </div>
      </div>

      {open && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          {group.orders.map(o => (
            <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: isMobile ? '9px 14px' : '10px 20px', borderTop: '1px solid rgba(255,255,255,0.03)', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: '#64748b', width: 60, flexShrink: 0 }}>{fmtDateShort(o.date)}</span>
              <StatusBadge status={o.status} stuck={isStuck(o)} />
              <span style={{ fontSize: 12, fontWeight: 600, color: '#f1f5f9', width: 70 }}>{o.amount || '—'}</span>
              <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#475569' }}>{o.orderId || ''}</span>
              <span style={{ fontSize: 11, color: '#94a3b8', flex: 1, minWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {o.address?.name ? `${o.address.name} — ` : ''}{shortAddress(o.address)}
              </span>
              {o.cancelledAt && <span style={{ fontSize: 10, color: '#f87171' }}>cancelled {fmtDateShort(o.cancelledAt)}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductsTab({ groups, isMobile }) {
  if (!groups.length) return null;
  return (
    <>
      {groups.map(g => <ProductCard key={g.key} group={g} isMobile={isMobile} />)}
    </>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function PokemonCenter() {
  const isMobile = useMobile();

  const [tab, setTab]         = useState('orders'); // orders | addresses
  const [phase, setPhase]     = useState('idle');   // idle | scanning | done
  const [orders, setOrders]   = useState([]);
  const [errors, setErrors]   = useState([]);
  const [scanProgress, setScanProgress] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');

  const [showInboxes, setShowInboxes] = useState(false);
  const [inboxFallback, setInboxFallback] = useState(false);

  // Defaults to the last 6 months
  const [sinceDate, setSinceDate] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 6); return toInputDate(d); });
  const [untilDate, setUntilDate] = useState(() => toInputDate(new Date()));

  useEffect(() => {
    fetch('/api/receipts/settings').then(r => r.json())
      .then(d => { if (d.ok) setInboxFallback(d.fallback); }).catch(() => {});
  }, [showInboxes]);

  // Previously scanned orders are persisted server-side — show them on load.
  useEffect(() => {
    fetch('/api/pokemon/orders').then(r => r.json())
      .then(d => {
        if (d.ok && d.orders.length) {
          setOrders(d.orders);
          setPhase('done');
        }
      }).catch(() => {});
  }, []);

  async function scan() {
    setPhase('scanning');
    setOrders([]); setErrors([]);

    const batches     = buildBatches(sinceDate, untilDate);
    const accumulated = new Map();
    const allErrors   = [];

    for (let i = 0; i < batches.length; i++) {
      const { since, until, label } = batches[i];
      setScanProgress({ batch: i + 1, total: batches.length, label });
      try {
        const res  = await fetch('/api/pokemon/scan', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ since, until, final: i === batches.length - 1 }),
        });
        const data = await res.json();
        if (!data.ok) throw new Error(data.error);
        // The server returns its full persisted store (all scans merged), so
        // it is the source of truth; the local map is just a fallback.
        mergeInto(accumulated, data.orders);
        allErrors.push(...(data.errors || []));
      } catch (err) {
        allErrors.push({ error: `${label}: ${err.message}` });
      }
    }

    setOrders([...accumulated.values()].sort((a, b) => new Date(b.date) - new Date(a.date)));
    setErrors(allErrors.slice(0, 5));
    setScanProgress(null);
    setPhase('done');
  }

  function downloadCsv() {
    const header = ['Date','OrderID','Status','Stuck','Total','Name','Address','Postcode','Account','Items','CancelledAt'].join(',');
    const q = v => `"${String(v || '').replace(/"/g, '""')}"`;
    const rows = filtered.map(o => [
      fmtDate(o.date), o.orderId || '', o.status, isStuck(o) ? 'yes' : '', o.amount || '',
      q(o.address?.name), q((o.address?.lines || []).join(', ')), o.address?.postcode || '',
      o.accountEmail || o.account, q((o.items || []).join(' | ')),
      o.cancelledAt ? fmtDate(o.cancelledAt) : '',
    ].join(','));
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `pokemon_center_orders_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  // Product filter — grouped by release so "Pitch Black" pulls the ETB, the
  // booster bundle and anything else from that drop together.
  const productFilters = useMemo(() => {
    const counts = new Map();
    orders.forEach(o => (o.items || []).forEach(i => {
      const m = i.match(/(?:Mega Evolution-|Scarlet & Violet-)?([A-Z][\w'’]*(?:\s+[A-Z][\w'’]*)*)/);
      const release = (i.match(/(Pitch Black|Chaos Rising|30th Celebration|Ascended Heroes|Perfect Order|Phantasmal Flames|Surging Sparks|White Flare)/i) || [])[1];
      const label = release || (m ? m[1] : null);
      if (label) counts.set(label, (counts.get(label) || 0) + 1);
    }));
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [orders]);

  const byProduct = productFilter === 'all'
    ? orders
    : orders.filter(o => (o.items || []).some(i => i.toLowerCase().includes(productFilter.toLowerCase())));

  const filtered = statusFilter === 'all' ? byProduct
    : statusFilter === 'cancelled' ? byProduct.filter(isCancelled)
    : statusFilter === 'payment'   ? byProduct.filter(isPaymentRisk)
    : statusFilter === 'stuck'     ? byProduct.filter(isStuck)
    : byProduct.filter(o => !isCancelled(o) && !isStuck(o) && !isPaymentRisk(o));
  const groups = buildAddressGroups(filtered);
  const productGroups = buildProductGroups(filtered);
  const isScanning = phase === 'scanning';

  const dateInputStyle = {
    background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
    color: '#e2e8f0', fontSize: 12, padding: '7px 10px', colorScheme: 'dark',
  };

  return (
    <div style={{ maxWidth: 960 }}>
      {/* ── Header ── */}
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, letterSpacing: '-0.5px', color: '#f1f5f9' }}>Pokémon Center</h1>
        <p style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>Scans inboxes for Pokémon Center order and cancellation emails — cancelled and stuck (no shipment after {STUCK_DAYS} days) orders, analysed by shipping address.</p>
      </div>

      {/* ── Stats ── */}
      {phase === 'done' && orders.length > 0 && <StatsRow orders={orders} groups={buildAddressGroups(orders)} />}

      {/* ── Controls ── */}
      <div style={{ ...CARD, padding: isMobile ? '14px' : '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
          From <input type="date" value={sinceDate} onChange={e => setSinceDate(e.target.value)} style={dateInputStyle} />
        </label>
        <label style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
          To <input type="date" value={untilDate} onChange={e => setUntilDate(e.target.value)} style={dateInputStyle} />
        </label>

        <div style={{ flex: 1 }} />

        <button onClick={() => setShowInboxes(s => !s)} style={{ fontSize: 12, borderRadius: 10, cursor: 'pointer', padding: '8px 14px', background: showInboxes ? '#334155' : 'rgba(255,255,255,0.03)', color: showInboxes ? '#e2e8f0' : '#64748b', border: '1px solid rgba(255,255,255,0.08)' }}>
          📥 Inboxes
        </button>

        {phase === 'done' && filtered.length > 0 && (
          <button onClick={downloadCsv} style={{ fontSize: 12, fontWeight: 500, borderRadius: 10, cursor: 'pointer', border: '1px solid rgba(52,211,153,0.3)', padding: '8px 16px', background: 'rgba(52,211,153,0.1)', color: '#34d399' }}>
            ↓ CSV
          </button>
        )}

        <button onClick={scan} disabled={isScanning} style={{
          fontSize: 13, fontWeight: 600, borderRadius: 10, border: 'none', padding: '9px 20px',
          background: isScanning ? '#1e293b' : 'linear-gradient(135deg, #ef4444, #f59e0b)',
          color: isScanning ? '#475569' : '#fff',
          cursor: isScanning ? 'not-allowed' : 'pointer',
          boxShadow: isScanning ? 'none' : '0 2px 8px rgba(239,68,68,0.35)',
        }}>
          {isScanning ? (scanProgress ? `Scanning ${scanProgress.label} (${scanProgress.batch}/${scanProgress.total})…` : 'Scanning…') : '↻ Scan'}
        </button>
      </div>

      {/* ── Inboxes panel (shared with the Receipt Scanner) ── */}
      {showInboxes && (
        <div style={{ ...CARD, padding: isMobile ? '16px' : '18px 22px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9', marginBottom: 4 }}>Inboxes</div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 14 }}>
            Shared with the Receipt Scanner.
            {inboxFallback && <span style={{ color: '#fcd34d' }}> Currently using the Order Dashboard's inboxes — add inboxes here to override.</span>}
          </div>
          <ImapSettings basePath="/api/receipts/settings" onSaved={() => {}} />
        </div>
      )}

      {/* ── Scan progress ── */}
      {isScanning && scanProgress && (
        <div style={{ ...CARD, padding: '14px 18px', marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 8 }}>
            <span>Scanning <strong style={{ color: '#94a3b8' }}>{scanProgress.label}</strong></span>
            <span>{scanProgress.batch} / {scanProgress.total}</span>
          </div>
          <div style={{ height: 4, background: 'rgba(255,255,255,0.05)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 4, background: 'linear-gradient(90deg, #ef4444, #f59e0b)', width: `${(scanProgress.batch / scanProgress.total) * 100}%`, transition: 'width 0.4s ease' }} />
          </div>
        </div>
      )}

      {/* ── Errors ── */}
      {errors.length > 0 && (
        <div style={{ background: 'rgba(220,38,38,0.12)', color: '#fca5a5', borderRadius: 10, padding: '11px 14px', fontSize: 12, marginBottom: 14, border: '1px solid rgba(220,38,38,0.25)', fontWeight: 500 }}>
          ⚠ {errors.map(e => e.account ? `${e.account}: ${e.error}` : e.error).join(' · ')}
        </div>
      )}

      {/* ── Results ── */}
      {phase === 'done' && orders.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: 4 }}>
              {[['orders', 'Orders'], ['products', `Products (${productGroups.length})`], ['addresses', `Addresses (${groups.length})`], ['patterns', 'Patterns']].map(([key, label]) => (
                <button key={key} onClick={() => setTab(key)} style={{
                  fontSize: 12, fontWeight: 600, padding: '7px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: tab === key ? '#334155' : 'transparent',
                  color: tab === key ? '#f1f5f9' : '#64748b',
                }}>{label}</button>
              ))}
            </div>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: 4, flexWrap: 'wrap' }}>
              {[['all', 'All'], ['kept', 'Kept'], ['cancelled', 'Cancelled'], ['payment', 'Payment'], ['stuck', 'Stuck']].map(([key, label]) => (
                <button key={key} onClick={() => setStatusFilter(key)} style={{
                  fontSize: 11, fontWeight: 600, padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: statusFilter === key ? '#334155' : 'transparent',
                  color: statusFilter === key ? '#f1f5f9' : '#64748b',
                }}>{label}</button>
              ))}
            </div>
          </div>

          {/* Product / release filter — pick a drop to see its per-address success rate */}
          {productFilters.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#64748b', marginRight: 2 }}>Product:</span>
              {[['all', `All (${orders.length})`], ...productFilters.map(([name, n]) => [name, `${name} (${n})`])].map(([key, label]) => (
                <button key={key} onClick={() => setProductFilter(key)} style={{
                  fontSize: 11, fontWeight: 600, padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
                  border: productFilter === key ? '1px solid rgba(129,140,248,0.5)' : '1px solid rgba(255,255,255,0.08)',
                  background: productFilter === key ? 'rgba(129,140,248,0.15)' : 'transparent',
                  color: productFilter === key ? '#a5b4fc' : '#64748b',
                }}>{label}</button>
              ))}
            </div>
          )}

          {tab === 'orders' && (
            filtered.length > 0
              ? <OrdersTable orders={filtered} isMobile={isMobile} />
              : <div style={{ ...CARD, padding: '32px', textAlign: 'center', color: '#64748b', fontSize: 13, marginBottom: 16 }}>No orders match this filter.</div>
          )}
          {tab === 'products' && <ProductsTab groups={productGroups} isMobile={isMobile} />}
          {tab === 'addresses' && <AddressesTab groups={groups} isMobile={isMobile} />}
          {tab === 'patterns'  && <PatternsTab orders={filtered} isMobile={isMobile} />}
        </>
      )}

      {phase === 'done' && orders.length === 0 && (
        <div style={{ ...CARD, padding: '32px', textAlign: 'center', color: '#64748b', fontSize: 13, marginBottom: 16 }}>
          No Pokémon Center emails found in this date range.
        </div>
      )}

      {/* ── Idle ── */}
      {phase === 'idle' && (
        <div style={{ ...CARD, padding: isMobile ? '48px 20px' : '64px 40px', textAlign: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 16 }}>⚡</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 10 }}>Scan for Pokémon Center orders</div>
          <div style={{ fontSize: 13, color: '#64748b', maxWidth: 440, margin: '0 auto', lineHeight: 1.6 }}>
            Pick a date range (defaults to the last 6 months) and hit <strong style={{ color: '#94a3b8' }}>Scan</strong>.
            Order confirmations and cancellations are matched by order number, and the
            <strong style={{ color: '#94a3b8' }}> Addresses</strong> tab shows the cancel rate per shipping address.
          </div>
        </div>
      )}
    </div>
  );
}
